import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { ErrorNote } from '@/components/ui/misc'
import { Dialog } from '@/components/ui/overlay'
import { api, ApiError } from '@/lib/api'

/**
 * Renames one file.
 *
 * The wording is deliberate about what happens underneath: there is no rename
 * on this storage, so the file is copied and the original deleted. The user
 * should not be surprised by a "both copies exist" outcome if the second step
 * fails.
 */
export function RenameDialog({
  bucket,
  objectKey,
  isLocked,
  lockReason,
  onClose,
  onRenamed,
}: {
  bucket: string
  objectKey: string
  isLocked: boolean
  lockReason: string | null
  onClose: () => void
  onRenamed: (newKey: string) => void
}) {
  const currentName = objectKey.split('/').pop() ?? objectKey
  const [name, setName] = useState(currentName)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    const trimmed = name.trim()
    if (!trimmed || trimmed === currentName) {
      onClose()
      return
    }

    setBusy(true)
    setError(null)
    try {
      const res = await api.renameObject(bucket, objectKey, trimmed)
      onRenamed(res.key)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not rename the file')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Rename file"
      description={
        <>
          Renaming copies the file to the new name and deletes the original — this storage has
          no rename operation.
        </>
      }
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={busy}
            disabled={isLocked || !name.trim() || name.trim() === currentName}
          >
            Rename
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}

        {isLocked && (
          <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger/10 px-3 py-2.5 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
            <div>
              <p className="font-medium text-danger">This file cannot be renamed</p>
              <p className="text-muted-foreground">
                {lockReason}. Renaming requires deleting the original, which HCP will not allow
                while it is protected.
              </p>
            </div>
          </div>
        )}

        <Field label="New name" htmlFor="rename-input" hint="The file stays in the same folder.">
          <Input
            id="rename-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLocked}
            autoFocus
            onFocus={(e) => {
              // Select the stem, leaving the extension alone — the part people
              // actually intend to change.
              const dot = e.currentTarget.value.lastIndexOf('.')
              e.currentTarget.setSelectionRange(0, dot > 0 ? dot : e.currentTarget.value.length)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isLocked) void submit()
            }}
          />
        </Field>

        {!isLocked && (
          <p className="text-xs text-muted-foreground">
            If the file is currently public, its link will stop working — the URL contains the
            filename, so a renamed file needs to be published again.
          </p>
        )}
      </div>
    </Dialog>
  )
}
